<template>
  <div class="min-h-full bg-transparent dark:bg-[#0B1120] pb-28 text-slate-800 dark:text-[#F1F5F9] font-sans antialiased">
    <main
      class="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8"
    >
      <!-- LOADING SKELETON -->
      <div
        v-if="isLoading"
        class="space-y-6"
      >
        <div class="h-48 w-full animate-pulse rounded-2xl bg-slate-100"></div>
        <div class="h-96 w-full animate-pulse rounded-2xl bg-slate-100"></div>
      </div>

      <!-- MAIN PAGE CONTENT -->
      <div
        v-else
        class="space-y-6 sm:space-y-8"
      >
        <!-- SECTION 1: REALTIME CANVAS PREVIEW (Hidden on mobile < md) -->
        <div class="hidden md:block space-y-3.5">
          <div class="flex items-center justify-between">
            <h3
              class="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"
            >
              <EyeIcon class="h-4 w-4 text-indigo-600" />
              Live Website Grid Preview
            </h3>
            <span
              class="rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700"
            >
              Realtime
            </span>
          </div>

          <!-- Academic Cards Grid Preview -->
          <div
            class="rounded-2xl border border-slate-200/80 bg-white p-5 sm:p-7 shadow-xs space-y-5"
          >
            <div class="max-w-md space-y-1">
              <span
                class="inline-block rounded-full bg-indigo-50 border border-indigo-100 px-3 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-indigo-600"
              >
                On today's timetable
              </span>
              <h2 class="text-xl font-black text-slate-900 tracking-tight">
                Academic Excellence
              </h2>
              <p class="text-xs text-slate-500 leading-relaxed">
                Three pillars that shape every student's day here — taught by
                mentors, not just teachers.
              </p>
            </div>

            <!-- Preview Cards Grid -->
            <div
              v-if="programsList.length > 0"
              class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
            >
              <div
                v-for="(prog, idx) in programsList"
                :key="prog._id || idx"
                class="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-slate-200/90 bg-white transition-all duration-200 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-lg"
                :class="{ 'opacity-50 grayscale': prog.active === false }"
              >
                <!-- Image Header -->
                <div class="relative h-36 w-full overflow-hidden bg-slate-50">
                  <img
                    :src="getProgramImageSrc(prog)"
                    :alt="prog.title"
                    class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span
                    class="absolute top-2.5 left-2.5 rounded-full bg-slate-900/80 px-2.5 py-0.5 font-mono text-[9px] font-bold tracking-wider text-amber-400 backdrop-blur-xs"
                  >
                    {{
                      prog.code ||
                      "PROGRAM " + (idx + 1 < 10 ? "0" + (idx + 1) : idx + 1)
                    }}
                  </span>
                  <span
                    v-if="prog.active === false"
                    class="absolute top-2.5 right-2.5 rounded-full bg-rose-500 px-2.5 py-0.5 text-[9px] font-bold text-white shadow-xs"
                  >
                    Disabled
                  </span>
                </div>

                <!-- Body Summary -->
                <div class="p-4 space-y-1.5 flex-1">
                  <h4
                    class="text-sm font-bold text-slate-900 line-clamp-1 group-hover:text-indigo-600 transition-colors"
                  >
                    {{ prog.title || "Untitled Program" }}
                  </h4>
                  <div
                    class="text-[11px] text-slate-500 leading-relaxed line-clamp-2 prose prose-xs"
                    v-html="
                      prog.description || 'No summary description provided.'
                    "
                  ></div>
                </div>

                <!-- Card Footer Link -->
                <div
                  class="border-t border-slate-100 bg-slate-50/60 px-4 py-2.5 flex items-center justify-between"
                >
                  <span
                    class="text-[10px] font-mono font-medium text-slate-400 truncate max-w-[180px]"
                  >
                    /academic/{{ prog.slug || generateSlug(prog.title) }}
                  </span>
                  <span
                    class="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 group-hover:translate-x-0.5 transition-transform"
                  >
                    Explore <ArrowRightIcon class="h-3 w-3" />
                  </span>
                </div>
              </div>
            </div>

            <!-- Empty Grid Preview -->
            <div
              v-else
              class="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center bg-slate-50/50"
            >
              <BookOpenIcon class="mx-auto h-8 w-8 text-slate-300" />
              <p class="mt-2 text-xs font-semibold text-slate-600">
                No Academic Programs Configured
              </p>
              <p class="text-[11px] text-slate-400">
                Add a program post using the manager below.
              </p>
            </div>
          </div>
        </div>

        <!-- SECTION 2: PROGRAM MANAGER TABLE -->
        <div
          class="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-xs"
        >
          <div
            class="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4"
          >
            <div class="flex items-center space-x-2.5">
              <BookOpenIcon class="h-5 w-5 text-indigo-600 flex-shrink-0" />
              <div>
                <h2 class="text-base font-bold text-slate-900">
                  Academic Posts Manager ({{ programsList.length }})
                </h2>
                <p class="text-xs text-slate-500">
                  Add, edit, manage program galleries, or reorder academic posts
                </p>
              </div>
            </div>
            <button
              type="button"
              class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-xs font-bold text-white shadow-md shadow-indigo-600/20 transition hover:bg-indigo-500 active:scale-95 cursor-pointer"
              @click="openAddModal"
            >
              <PlusIcon class="h-4 w-4" />
              <span>Add New Program</span>
            </button>
          </div>

          <!-- Programs List -->
          <div
            v-if="programsList.length > 0"
            class="space-y-3 sm:space-y-4"
          >
            <div
              v-for="(prog, index) in programsList"
              :key="prog._id || index"
              class="relative rounded-2xl border border-slate-200/90 bg-white p-3.5 sm:p-4 transition-all hover:border-indigo-200 hover:shadow-xs"
            >
              <div
                class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <!-- Left Details -->
                <div class="flex items-center space-x-3.5 min-w-0">
                  <div
                    class="relative h-14 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-slate-50 border border-slate-200"
                  >
                    <img
                      :src="getProgramImageSrc(prog)"
                      :alt="prog.title"
                      class="h-full w-full object-cover"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2">
                      <span
                        class="rounded-md bg-indigo-50 px-2 py-0.5 font-mono text-[9px] font-bold text-indigo-600 border border-indigo-100"
                      >
                        {{ prog.code || "ACADEMIC PROGRAM" }}
                      </span>
                      <span
                        v-if="prog.images && prog.images.length > 0"
                        class="rounded-md bg-slate-100 px-2 py-0.5 text-[9px] font-bold text-slate-600 flex items-center gap-1"
                      >
                        <ImageIcon class="h-2.5 w-2.5" />
                        {{ prog.images.length }} Photos
                      </span>
                      <span
                        v-if="prog.active === false"
                        class="rounded-md bg-rose-50 px-2 py-0.5 text-[9px] font-bold text-rose-600 border border-rose-100"
                      >
                        Hidden
                      </span>
                    </div>
                    <h3 class="mt-1 text-sm font-bold text-slate-900 truncate">
                      {{ prog.title || "Untitled Program" }}
                    </h3>
                    <p class="text-xs font-mono text-slate-400 truncate">
                      /academic/{{ prog.slug || generateSlug(prog.title) }}
                    </p>
                  </div>
                </div>

                <!-- Right Controls -->
                <div
                  class="flex items-center justify-between sm:justify-end gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100"
                >
                  <div class="flex items-center space-x-1">
                    <button
                      type="button"
                      class="p-1.5 text-slate-400 hover:text-indigo-600 disabled:opacity-20 cursor-pointer active:scale-95"
                      :disabled="index === 0"
                      title="Move Up"
                      @click="moveProgram(index, -1)"
                    >
                      <ChevronUpIcon class="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      class="p-1.5 text-slate-400 hover:text-indigo-600 disabled:opacity-20 cursor-pointer active:scale-95"
                      :disabled="index === programsList.length - 1"
                      title="Move Down"
                      @click="moveProgram(index, 1)"
                    >
                      <ChevronDownIcon class="h-4 w-4" />
                    </button>
                  </div>

                  <div class="flex items-center space-x-2">
                    <button
                      type="button"
                      class="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100 transition cursor-pointer active:scale-95"
                      @click="editProgram(index)"
                    >
                      <Edit3Icon class="h-3.5 w-3.5 text-indigo-600" />
                      <span>Edit Post</span>
                    </button>
                    <button
                      type="button"
                      class="p-1.5 text-slate-400 hover:text-rose-600 transition cursor-pointer active:scale-90"
                      title="Remove Program"
                      @click="removeProgram(index)"
                    >
                      <Trash2Icon class="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty List State -->
          <div
            v-else
            class="text-center py-10"
          >
            <p class="text-xs text-slate-500">
              No academic posts configured yet. Click above to add your first
              program.
            </p>
          </div>
        </div>
      </div>

      <!-- COMPACT FLOATING ACTION BAR (STICKY TO BOTTOM RIGHT ALWAYS) -->
      <teleport to="body">
        <div
          class="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-8 z-[900] max-w-[calc(100vw-2rem)] sm:max-w-lg pointer-events-auto"
        >
          <div
            class="rounded-2xl bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] p-2.5 sm:p-3 shadow-2xl shadow-slate-900/15 flex items-center justify-between gap-3 sm:gap-4 ring-1 ring-slate-950/5 dark:ring-white/5"
          >
            <!-- Status Indicator (Compact) -->
            <div class="flex items-center gap-2 text-xs font-medium min-w-0">
              <span
                v-if="isSaving"
                class="inline-flex items-center gap-1.5 text-indigo-600 dark:text-blue-400 font-semibold truncate"
              >
                <Loader2Icon
                  class="h-4 w-4 animate-spin shrink-0 text-indigo-600 dark:text-blue-400"
                />
                <span class="truncate">Saving...</span>
              </span>

              <span
                v-else-if="hasUnsavedChanges"
                class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/50 px-2.5 py-1 border border-amber-200/80 dark:border-amber-800/40 text-amber-700 dark:text-amber-400 font-bold shrink-0"
              >
                <span
                  class="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
                ></span>
                <span>Unsaved</span>
              </span>

              <span
                v-else
                class="inline-flex items-center gap-1.5 text-slate-500 dark:text-[#A6B4C8] truncate"
              >
                <CheckCircleIcon class="h-4 w-4 text-emerald-500 shrink-0" />
                <span class="hidden sm:inline text-xs truncate"
                  >All changes saved</span
                >
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                class="rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-100 dark:hover:bg-[#1F2E47] transition active:scale-95 cursor-pointer"
                :disabled="isSaving"
                @click="confirmReset"
              >
                Discard
              </button>

              <!-- Primary Save Action Button -->
              <button
                type="button"
                class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-4 sm:px-5 py-2 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                :disabled="isSaving"
                @click="handleSave"
              >
                <Loader2Icon
                  v-if="isSaving"
                  class="h-3.5 w-3.5 animate-spin"
                />
                <SaveIcon
                  v-else
                  class="h-3.5 w-3.5"
                />
                <span class="whitespace-nowrap">{{
                  isSaving ? "Saving" : "Save Academic Programs"
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </teleport>
    </main>

    <!-- FULL-SCREEN POST EDITOR MODAL -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-md flex flex-col justify-between overflow-hidden"
    >
      <!-- TOP HEADER BAR -->
      <div
        class="h-14 bg-slate-900 border-b border-slate-800 px-4 flex items-center justify-between text-white flex-shrink-0"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center font-black text-sm shadow-md shadow-indigo-600/30"
          >
            W
          </div>
          <div>
            <h2 class="text-xs font-bold leading-tight">
              {{
                editingIndex === -1
                  ? "New Academic Post"
                  : activeForm.title || "Edit Post"
              }}
            </h2>
            <p class="text-[10px] text-slate-400 font-mono">
              /academic/{{ activeForm.slug || generateSlug(activeForm.title) }}
            </p>
          </div>
        </div>

        <!-- Header Actions -->
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 transition cursor-pointer"
            @click="toggleSidebar"
          >
            <SidebarIcon class="h-3.5 w-3.5" />
            <span>{{ isSidebarOpen ? "Hide Settings" : "Post Settings" }}</span>
          </button>

          <button
            type="button"
            class="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition active:scale-95 cursor-pointer shadow-md shadow-indigo-600/30"
            @click="saveModalForm"
          >
            Done & Apply
          </button>

          <button
            type="button"
            class="p-1.5 text-slate-400 hover:text-white rounded-xl transition cursor-pointer"
            @click="closeModal"
          >
            <XIcon class="h-5 w-5" />
          </button>
        </div>
      </div>

      <!-- EDITOR BODY FLEX CONTAINER -->
      <div class="flex-1 flex overflow-hidden bg-slate-50">
        <!-- MOBILE LIGHT FORM EDITOR (Shown ONLY on < md viewports) -->
        <div class="md:hidden flex-1 overflow-y-auto p-4 space-y-5 bg-white">
          <div
            class="rounded-xl bg-indigo-50/60 p-3 border border-indigo-100 text-xs text-indigo-800 flex items-center gap-2"
          >
            <AlertCircleIcon class="h-4 w-4 shrink-0 text-indigo-600" />
            <span
              >Light Touch Editor enabled for mobile. Switch to desktop for full
              block canvas editing.</span
            >
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Program Title *</label
            >
            <input
              v-model="activeForm.title"
              type="text"
              placeholder="e.g. Senior Secondary Science"
              class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none"
              @input="autoGenerateSlug"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >URL Slug</label
            >
            <input
              v-model="activeForm.slug"
              type="text"
              placeholder="senior-secondary-science"
              class="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-mono text-slate-800 focus:border-indigo-500 focus:outline-none"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Program Code Tag</label
            >
            <input
              v-model="activeForm.code"
              type="text"
              placeholder="SUBJ / SCIENCE"
              class="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-mono text-slate-800 focus:border-indigo-500 focus:outline-none"
            />
          </div>

          <!-- Featured Cover Photo -->
          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Featured Cover Image</label
            >
            <div
              class="h-32 w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50 mb-2"
            >
              <img
                :src="activeForm.previewImage || fallbackImage"
                class="h-full w-full object-cover"
              />
            </div>
            <input
              type="file"
              accept="image/*"
              class="w-full text-xs text-slate-500 file:mr-2 file:rounded-xl file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-indigo-600"
              @change="handleImageFile"
            />
          </div>

          <!-- Mobile Program Gallery Manager -->
          <div class="space-y-2 pt-2 border-t border-slate-100">
            <div class="flex items-center justify-between">
              <div>
                <label class="block text-xs font-bold text-slate-700"
                  >Program Gallery Photos</label
                >
                <p class="text-[10px] text-slate-400">
                  Add multiple activity & classroom photos
                </p>
              </div>
              <button
                type="button"
                class="px-2.5 py-1 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 text-xs font-bold rounded-lg transition"
                @click="triggerGalleryUpload"
              >
                + Add Photos
              </button>
            </div>

            <input
              ref="galleryFileInput"
              type="file"
              accept="image/*"
              multiple
              class="hidden"
              @change="handleGalleryImagesSelected"
            />

            <div
              v-if="activeForm.images && activeForm.images.length > 0"
              class="grid grid-cols-3 gap-2"
            >
              <div
                v-for="(img, gIdx) in activeForm.images"
                :key="gIdx"
                class="relative h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 group"
              >
                <img
                  :src="img"
                  class="w-full h-full object-cover"
                />
                <button
                  type="button"
                  class="absolute top-1 right-1 p-1 bg-slate-900/80 text-white rounded-full hover:bg-rose-600 transition"
                  @click="removeGalleryImage(gIdx)"
                >
                  <XIcon class="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Short Excerpt / Summary</label
            >
            <textarea
              v-model="activeForm.description"
              rows="3"
              placeholder="Brief summary displayed on grid cards..."
              class="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
            ></textarea>
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Main Program Description</label
            >
            <textarea
              v-model="activeForm.content"
              rows="6"
              placeholder="Full details of curriculum, faculty, and labs..."
              class="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
            ></textarea>
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Visibility</label
            >
            <select
              v-model="activeForm.active"
              class="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
            >
              <option :value="true">Published (Active)</option>
              <option :value="false">Draft (Hidden)</option>
            </select>
          </div>
        </div>

        <!-- DESKTOP GUTENBERG BLOCK EDITOR CANVAS (Shown ONLY on >= md viewports) -->
        <div
          class="hidden md:flex flex-1 overflow-y-auto p-4 sm:p-8 md:p-12 justify-center"
        >
          <div
            class="w-full max-w-3xl bg-white border border-slate-200/90 shadow-md rounded-2xl p-6 sm:p-10 space-y-6 self-start"
          >
            <!-- Post Title Block -->
            <div>
              <input
                v-model="activeForm.title"
                type="text"
                placeholder="Add Program Title..."
                class="w-full text-2xl sm:text-3xl font-black text-slate-900 border-b border-transparent hover:border-slate-200 focus:border-indigo-600 outline-none transition py-2"
                @input="autoGenerateSlug"
              />
            </div>

            <!-- WORDPRESS GUTENBERG TOOLBAR -->
            <div
              class="sticky top-0 z-20 bg-slate-900/95 backdrop-blur-md text-slate-200 rounded-xl p-1.5 shadow-xl flex flex-wrap items-center gap-1 border border-slate-800"
            >
              <!-- Heading Selectors -->
              <button
                type="button"
                @click="formatDoc('formatBlock', 'h1')"
                class="px-2 py-1 text-xs font-bold rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
              >
                H1
              </button>
              <button
                type="button"
                @click="formatDoc('formatBlock', 'h2')"
                class="px-2 py-1 text-xs font-bold rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
              >
                H2
              </button>
              <button
                type="button"
                @click="formatDoc('formatBlock', 'h3')"
                class="px-2 py-1 text-xs font-bold rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
              >
                H3
              </button>
              <button
                type="button"
                @click="formatDoc('formatBlock', 'p')"
                class="px-2 py-1 text-xs font-bold rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
              >
                P
              </button>

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <!-- Basic Formatting -->
              <button
                type="button"
                @click="formatDoc('bold')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Bold"
              >
                <BoldIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('italic')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Italic"
              >
                <ItalicIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('underline')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Underline"
              >
                <span class="font-bold underline text-xs">U</span>
              </button>
              <button
                type="button"
                @click="formatDoc('strikeThrough')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Strikethrough"
              >
                <span class="font-bold line-through text-xs">S</span>
              </button>

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <!-- Lists & Quotes -->
              <button
                type="button"
                @click="formatDoc('insertUnorderedList')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Bullet List"
              >
                <ListIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('insertOrderedList')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Ordered List"
              >
                <ListOrderedIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('formatBlock', 'blockquote')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Blockquote"
              >
                <QuoteIcon class="h-3.5 w-3.5" />
              </button>

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <!-- Advanced Tools -->
              <button
                type="button"
                @click="promptInsertLink"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Insert Link"
              >
                <LinkIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('insertHorizontalRule')"
                class="px-2 py-1 text-[10px] font-mono font-bold rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Horizontal Divider"
              >
                ---
              </button>

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <!-- IN-CANVAS MULTIPLE PHOTOS UPLOAD BUTTON -->
              <button
                type="button"
                class="px-2.5 py-1 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 hover:text-amber-300 transition flex items-center gap-1.5 cursor-pointer active:scale-95 border border-amber-500/20"
                title="Insert One or Multiple Photos directly into Content"
                @click="triggerInEditorImageUpload"
              >
                <ImageIcon class="h-3.5 w-3.5" />
                <span class="text-[10px] font-bold uppercase tracking-wide">
                  + Add Photos
                </span>
              </button>

              <input
                ref="inEditorFileInput"
                type="file"
                accept="image/*"
                multiple
                class="hidden"
                @change="handleInEditorImagesSelected"
              />

              <div class="h-4 w-px bg-slate-800 mx-1"></div>

              <button
                type="button"
                @click="formatDoc('undo')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Undo"
              >
                <UndoIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                @click="formatDoc('redo')"
                class="p-1.5 rounded-lg hover:bg-slate-800 hover:text-white transition cursor-pointer active:scale-95"
                title="Redo"
              >
                <RedoIcon class="h-3.5 w-3.5" />
              </button>
            </div>

            <!-- FLOATING ACTIVE IMAGE CONTEXT TOOLBAR -->
            <Transition name="fade">
              <div
                v-if="selectedImageEl"
                class="bg-slate-900 text-white rounded-xl p-2 shadow-2xl border border-slate-800 flex items-center gap-3 text-xs"
              >
                <span
                  class="text-[10px] font-bold text-amber-400 uppercase tracking-wider pl-1"
                >
                  Width:
                </span>
                <div
                  class="flex items-center gap-1 bg-slate-800 rounded-lg p-0.5"
                >
                  <button
                    type="button"
                    @click="setSelectedImageWidth('25%')"
                    class="px-2 py-0.5 rounded text-[10px] transition cursor-pointer hover:bg-slate-700"
                  >
                    25%
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageWidth('50%')"
                    class="px-2 py-0.5 rounded text-[10px] transition cursor-pointer hover:bg-slate-700"
                  >
                    50%
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageWidth('75%')"
                    class="px-2 py-0.5 rounded text-[10px] transition cursor-pointer hover:bg-slate-700"
                  >
                    75%
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageWidth('100%')"
                    class="px-2 py-0.5 rounded text-[10px] transition cursor-pointer hover:bg-slate-700"
                  >
                    100%
                  </button>
                </div>

                <div class="h-4 w-px bg-slate-800"></div>

                <span
                  class="text-[10px] font-bold text-amber-400 uppercase tracking-wider"
                >
                  Align:
                </span>
                <div
                  class="flex items-center gap-1 bg-slate-800 rounded-lg p-0.5"
                >
                  <button
                    type="button"
                    @click="setSelectedImageAlign('left')"
                    class="p-1 rounded transition cursor-pointer hover:bg-slate-700"
                    title="Align Left"
                  >
                    <AlignLeftIcon class="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageAlign('center')"
                    class="p-1 rounded transition cursor-pointer hover:bg-slate-700"
                    title="Align Center"
                  >
                    <AlignCenterIcon class="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    @click="setSelectedImageAlign('right')"
                    class="p-1 rounded transition cursor-pointer hover:bg-slate-700"
                    title="Align Right"
                  >
                    <AlignRightIcon class="h-3.5 w-3.5" />
                  </button>
                </div>

                <div class="h-4 w-px bg-slate-800"></div>

                <button
                  type="button"
                  @click="deleteSelectedImage"
                  class="p-1 text-rose-400 hover:text-rose-300 transition cursor-pointer ml-auto"
                  title="Remove Image"
                >
                  <Trash2Icon class="h-3.5 w-3.5" />
                </button>
              </div>
            </Transition>

            <!-- RICH TEXT CANVAS -->
            <div
              class="min-h-[360px] bg-slate-50/50 p-5 rounded-xl border border-slate-200/80 focus-within:border-indigo-600 transition"
            >
              <div
                ref="editableCanvas"
                contenteditable="true"
                class="prose prose-slate max-w-none focus:outline-none min-h-[320px] editor-body"
                @input="onEditorInput"
                @click="onCanvasClick"
                @keyup="saveSelection"
                @mouseup="saveSelection"
                @blur="saveSelection"
              ></div>
            </div>
          </div>
        </div>

        <!-- WORDPRESS RIGHT DOCUMENT SETTINGS SIDEBAR (Desktop Only) -->
        <div
          v-if="isSidebarOpen"
          class="hidden md:block w-80 bg-white border-l border-slate-200/80 overflow-y-auto p-5 space-y-6 flex-shrink-0"
        >
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400">
            Post Settings
          </h3>

          <!-- URL Slug -->
          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >URL Slug</label
            >
            <div class="relative">
              <span
                class="absolute left-2.5 top-2 text-[11px] font-mono text-slate-400"
                >/academic/</span
              >
              <input
                v-model="activeForm.slug"
                type="text"
                placeholder="slug"
                class="w-full pl-22 pr-2.5 py-1.5 text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
              />
            </div>
          </div>

          <!-- Code Tag & Status -->
          <div class="space-y-3">
            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1"
                >Program Code Tag</label
              >
              <input
                v-model="activeForm.code"
                type="text"
                placeholder="SUBJ / ARTS"
                class="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1"
                >Visibility State</label
              >
              <select
                v-model="activeForm.active"
                class="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
              >
                <option :value="true">Published (Active)</option>
                <option :value="false">Draft (Hidden)</option>
              </select>
            </div>
          </div>

          <!-- Cover Featured Image -->
          <div class="space-y-2">
            <label class="block text-xs font-bold text-slate-700"
              >Featured Image</label
            >
            <div
              class="h-28 w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
            >
              <img
                :src="activeForm.previewImage || fallbackImage"
                class="h-full w-full object-cover"
              />
            </div>
            <input
              type="file"
              accept="image/*"
              class="w-full text-[11px] text-slate-500 file:mr-2 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-2.5 file:py-1 file:text-[11px] file:font-bold file:text-indigo-600 hover:file:bg-indigo-100 cursor-pointer"
              @change="handleImageFile"
            />
          </div>

          <!-- Program Gallery Photos (Desktop Sidebar) -->
          <div class="space-y-2 pt-3 border-t border-slate-100">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-bold text-slate-700"
                  >Program Gallery</span
                >
                <p class="text-[10px] text-slate-400">
                  Photos rendered on program page
                </p>
              </div>
              <button
                type="button"
                class="text-[11px] font-bold text-indigo-600 hover:text-indigo-700 cursor-pointer"
                @click="triggerGalleryUpload"
              >
                + Add Photos
              </button>
            </div>

            <div
              v-if="activeForm.images && activeForm.images.length > 0"
              class="grid grid-cols-2 gap-2 mt-2"
            >
              <div
                v-for="(img, gIdx) in activeForm.images"
                :key="gIdx"
                class="relative h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group"
              >
                <img
                  :src="img"
                  class="w-full h-full object-cover"
                />
                <button
                  type="button"
                  class="absolute top-1 right-1 p-1 bg-slate-900/80 text-white rounded-full hover:bg-rose-600 transition"
                  title="Remove Image"
                  @click="removeGalleryImage(gIdx)"
                >
                  <XIcon class="h-3 w-3" />
                </button>
              </div>
            </div>

            <p
              v-else
              class="text-[11px] text-slate-400 italic py-1"
            >
              No gallery images added yet.
            </p>
          </div>

          <!-- Excerpt Description -->
          <div>
            <label class="block text-xs font-bold text-slate-700 mb-1"
              >Card Excerpt Summary</label
            >
            <textarea
              v-model="activeForm.description"
              rows="3"
              placeholder="Short excerpt for website.ejs grid..."
              class="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
            ></textarea>
          </div>

          <!-- Key Features -->
          <div class="space-y-2 pt-3 border-t border-slate-100">
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-slate-700">Key Benefits</span>
              <button
                type="button"
                class="text-[11px] font-bold text-indigo-600 cursor-pointer"
                @click="addFeature"
              >
                + Add
              </button>
            </div>
            <div
              v-for="(feat, fIdx) in activeForm.keyFeatures"
              :key="fIdx"
              class="p-2.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5 relative"
            >
              <button
                type="button"
                class="absolute top-1 right-1 text-rose-500 hover:text-rose-700 p-0.5 cursor-pointer"
                @click="removeFeature(fIdx)"
              >
                <Trash2Icon class="h-3 w-3" />
              </button>
              <input
                v-model="feat.title"
                type="text"
                placeholder="Benefit Title"
                class="w-full px-2 py-1 text-[11px] font-bold bg-white border border-slate-200 rounded outline-none"
              />
              <input
                v-model="feat.text"
                type="text"
                placeholder="Benefit details..."
                class="w-full px-2 py-1 text-[11px] bg-white border border-slate-200 rounded outline-none"
              />
            </div>
          </div>

          <!-- Sidebar Highlights -->
          <div class="space-y-2 pt-3 border-t border-slate-100">
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-slate-700">Highlights</span>
              <button
                type="button"
                class="text-[11px] font-bold text-indigo-600 cursor-pointer"
                @click="addHighlight"
              >
                + Add
              </button>
            </div>
            <div
              v-for="(hl, hIdx) in activeForm.highlights"
              :key="hIdx"
              class="flex items-center gap-1.5"
            >
              <input
                v-model="activeForm.highlights[hIdx]"
                type="text"
                placeholder="Highlight text"
                class="flex-1 px-2 py-1 text-[11px] bg-slate-50 border border-slate-200 rounded outline-none"
              />
              <button
                type="button"
                class="text-rose-500 p-0.5 cursor-pointer"
                @click="removeHighlight(hIdx)"
              >
                <Trash2Icon class="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-24 right-4 sm:right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-2xl bg-white p-5 sm:p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset all academic programs back to their
          saved state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {
    ref,
    reactive,
    computed,
    onMounted,
    onBeforeUnmount,
    watch,
    nextTick,
  } from "vue";
  import { useStore } from "vuex";
  import {
    Eye as EyeIcon,
    BookOpen as BookOpenIcon,
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    ChevronUp as ChevronUpIcon,
    ChevronDown as ChevronDownIcon,
    ArrowRight as ArrowRightIcon,
    Edit3 as Edit3Icon,
    X as XIcon,
    Bold as BoldIcon,
    Italic as ItalicIcon,
    List as ListIcon,
    ListOrdered as ListOrderedIcon,
    Quote as QuoteIcon,
    Image as ImageIcon,
    Undo as UndoIcon,
    Redo as RedoIcon,
    PanelRightClose as SidebarIcon,
    AlignLeft as AlignLeftIcon,
    AlignCenter as AlignCenterIcon,
    AlignRight as AlignRightIcon,
    Link as LinkIcon,
  } from "lucide-vue-next";

  const store = useStore();

  const fallbackImage =
    "https://dpstezpur.com/wp-content/uploads/elementor/thumbs/Classroom1-qqydxco77xhy2vmk97e1qpbnb7y3o3f6ym76f7x4kc.jpg";

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);
  const isModalOpen = ref(false);
  const isSidebarOpen = ref(true);
  const editingIndex = ref(-1);

  const editableCanvas = ref(null);
  const inEditorFileInput = ref(null);
  const galleryFileInput = ref(null);
  const selectedImageEl = ref(null);

  const programsList = ref([]);
  const initialStateJson = ref("");

  let savedSelectionRange = null;

  const activeForm = reactive({
    title: "",
    slug: "",
    code: "SUBJ / ACADEMICS",
    description: "",
    overview: "",
    content: "",
    image: "",
    imageFile: null,
    previewImage: "",
    images: [],
    keyFeatures: [],
    highlights: [],
    active: true,
  });

  /* =========================================
    SELECTION PRESERVATION & RESTORATION
  ========================================= */
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (
        editableCanvas.value &&
        editableCanvas.value.contains(range.commonAncestorContainer)
      ) {
        savedSelectionRange = range.cloneRange();
      }
    }
  };

  const restoreSelection = () => {
    if (savedSelectionRange) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRange);
    }
  };

  /* =========================================
    RICH TEXT EDITOR CONTROLS & SELECTION
  ========================================= */
  const formatDoc = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    if (editableCanvas.value) {
      activeForm.content = editableCanvas.value.innerHTML;
    }
    saveSelection();
  };

  const promptInsertLink = () => {
    const url = prompt("Enter Target URL (e.g. https://domain.com):");
    if (url) {
      formatDoc("createLink", url);
    }
  };

  const onEditorInput = () => {
    if (editableCanvas.value) {
      activeForm.content = editableCanvas.value.innerHTML;
    }
  };

  const onCanvasClick = (e) => {
    saveSelection();
    if (e.target && e.target.tagName === "IMG") {
      selectedImageEl.value = e.target;
    } else {
      selectedImageEl.value = null;
    }
  };

  const setSelectedImageWidth = (widthStr) => {
    if (selectedImageEl.value) {
      selectedImageEl.value.style.width = widthStr;
      onEditorInput();
    }
  };

  const setSelectedImageAlign = (alignStr) => {
    if (selectedImageEl.value) {
      if (alignStr === "left") {
        selectedImageEl.value.style.marginLeft = "0";
        selectedImageEl.value.style.marginRight = "auto";
      } else if (alignStr === "right") {
        selectedImageEl.value.style.marginLeft = "auto";
        selectedImageEl.value.style.marginRight = "0";
      } else {
        selectedImageEl.value.style.marginLeft = "auto";
        selectedImageEl.value.style.marginRight = "auto";
      }
      onEditorInput();
    }
  };

  const deleteSelectedImage = () => {
    if (selectedImageEl.value) {
      selectedImageEl.value.remove();
      selectedImageEl.value = null;
      onEditorInput();
    }
  };

  const syncEditorDOM = () => {
    selectedImageEl.value = null;
    savedSelectionRange = null;
    nextTick(() => {
      if (editableCanvas.value) {
        editableCanvas.value.innerHTML = activeForm.content || "<p></p>";
      }
    });
  };

  watch(isModalOpen, (isOpen) => {
    if (isOpen) {
      syncEditorDOM();
    }
  });

  /* IN-CANVAS IMAGE UPLOAD & NODE INSERTION */
  const triggerInEditorImageUpload = () => {
    saveSelection();
    if (inEditorFileInput.value) {
      inEditorFileInput.value.click();
    }
  };

  const insertNodeAtCaret = (node) => {
    restoreSelection();
    const sel = window.getSelection();

    if (
      sel &&
      sel.rangeCount > 0 &&
      editableCanvas.value &&
      editableCanvas.value.contains(sel.anchorNode)
    ) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(node);

      range.setStartAfter(node);
      range.setEndAfter(node);
      sel.removeAllRanges();
      sel.addRange(range);
    } else if (editableCanvas.value) {
      editableCanvas.value.appendChild(node);
    }

    onEditorInput();
    saveSelection();
  };

  const handleInEditorImagesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Src = event.target?.result;
        if (base64Src) {
          const img = document.createElement("img");
          img.src = base64Src;
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.borderRadius = "0.75rem";
          img.style.margin = "1rem 0";
          img.style.border = "1px solid #e2e8f0";
          img.style.display = "block";
          img.style.cursor = "pointer";

          insertNodeAtCaret(img);
        }
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  /* PROGRAM GALLERY MANAGER */
  const triggerGalleryUpload = () => {
    if (galleryFileInput.value) {
      galleryFileInput.value.click();
    }
  };

  const handleGalleryImagesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Src = event.target?.result;
        if (base64Src) {
          activeForm.images.push(base64Src);
        }
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  const removeGalleryImage = (index) => {
    activeForm.images.splice(index, 1);
  };

  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value;
  };

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  const generateSlug = (text) => {
    if (!text) return "";
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const autoGenerateSlug = () => {
    if (editingIndex.value === -1 || !activeForm.slug) {
      activeForm.slug = generateSlug(activeForm.title);
    }
  };

  const getProgramImageSrc = (prog) => {
    if (prog.imageFile instanceof File) {
      return URL.createObjectURL(prog.imageFile);
    }
    return prog.previewImage || prog.image || fallbackImage;
  };

  const getComparableImage = (img) => {
    if (img instanceof File) return img.name;
    return img || "";
  };

  const hasUnsavedChanges = computed(() => {
    const currentState = JSON.stringify(
      programsList.value.map((p) => ({
        ...p,
        image: getComparableImage(p.image),
        imageFile: getComparableImage(p.imageFile),
        images: Array.isArray(p.images) ? p.images : [],
      })),
    );
    return currentState !== initialStateJson.value;
  });

  const snapshotState = () => {
    initialStateJson.value = JSON.stringify(
      programsList.value.map((p) => ({
        ...p,
        image: getComparableImage(p.image),
        imageFile: getComparableImage(p.imageFile),
        images: Array.isArray(p.images) ? p.images : [],
      })),
    );
  };

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details =
        store.state.website?.details || store.state.websiteDetails || {};

      if (Array.isArray(details.academicPrograms)) {
        programsList.value = details.academicPrograms.map((p) => ({
          ...p,
          images: Array.isArray(p.images) ? [...p.images] : [],
          keyFeatures: Array.isArray(p.keyFeatures)
            ? JSON.parse(JSON.stringify(p.keyFeatures))
            : [],
          highlights: Array.isArray(p.highlights) ? [...p.highlights] : [],
          active: p.active !== false,
        }));
      } else {
        programsList.value = [];
      }

      snapshotState();
    } catch (err) {
      showToast("Failed to load website details", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const openAddModal = () => {
    editingIndex.value = -1;

    Object.assign(activeForm, {
      title: "",
      slug: "",
      code: "SUBJ / ACADEMICS",
      description: "",
      overview: "",
      content: "<p>Start writing rich content here...</p>",
      image: "",
      imageFile: null,
      previewImage: "",
      images: [],
      keyFeatures: [
        {
          icon: "fa-brain",
          title: "Conceptual Mastery",
          text: "In-depth understanding of fundamental principles.",
        },
      ],
      highlights: ["CBSE Aligned Modern Syllabus"],
      active: true,
    });

    isModalOpen.value = true;
    syncEditorDOM();
  };

  const editProgram = (index) => {
    editingIndex.value = index;

    const target = programsList.value[index];
    Object.assign(activeForm, {
      ...target,
      imageFile: target.imageFile || null,
      previewImage: getProgramImageSrc(target),
      images: Array.isArray(target.images) ? [...target.images] : [],
      keyFeatures: target.keyFeatures
        ? JSON.parse(JSON.stringify(target.keyFeatures))
        : [],
      highlights: target.highlights ? [...target.highlights] : [],
      active: target.active !== false,
    });

    isModalOpen.value = true;
    syncEditorDOM();
  };

  const closeModal = () => {
    isModalOpen.value = false;
  };

  const handleImageFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      activeForm.imageFile = file;
      activeForm.previewImage = URL.createObjectURL(file);
    }
  };

  const addFeature = () => {
    activeForm.keyFeatures.push({ icon: "fa-check", title: "", text: "" });
  };

  const removeFeature = (fIdx) => {
    activeForm.keyFeatures.splice(fIdx, 1);
  };

  const addHighlight = () => {
    activeForm.highlights.push("");
  };

  const removeHighlight = (hIdx) => {
    activeForm.highlights.splice(hIdx, 1);
  };

  const saveModalForm = () => {
    if (!activeForm.title.trim()) {
      showToast("Program title is required", "error");
      return;
    }

    if (editableCanvas.value) {
      activeForm.content = editableCanvas.value.innerHTML;
    }

    const formData = {
      ...activeForm,
      slug: activeForm.slug || generateSlug(activeForm.title),
      image: activeForm.imageFile || activeForm.image,
      images: [...activeForm.images],
      keyFeatures: [...activeForm.keyFeatures],
      highlights: activeForm.highlights.filter(Boolean),
    };

    if (editingIndex.value === -1) {
      programsList.value.push(formData);
    } else {
      programsList.value[editingIndex.value] = formData;
    }

    closeModal();
  };

  const removeProgram = (index) => {
    programsList.value.splice(index, 1);
  };

  const moveProgram = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= programsList.value.length) return;
    const item = programsList.value.splice(index, 1)[0];
    programsList.value.splice(targetIndex, 0, item);
  };

  const handleSave = async () => {
    isSaving.value = true;
    try {
      const details =
        store.state.website?.details || store.state.websiteDetails || {};

      const payload = {
        ...details,
        academicPrograms: programsList.value.map((p, idx) => ({
          ...p,
          sortOrder: idx,
        })),
      };

      const response = await store.dispatch("updateWebsiteDetails", payload);
      const updatedSchool = response?.school || store.state.website?.details;

      if (updatedSchool && Array.isArray(updatedSchool.academicPrograms)) {
        programsList.value = updatedSchool.academicPrograms.map((p) => ({
          ...p,
          images: Array.isArray(p.images) ? [...p.images] : [],
          keyFeatures: Array.isArray(p.keyFeatures)
            ? JSON.parse(JSON.stringify(p.keyFeatures))
            : [],
          highlights: Array.isArray(p.highlights) ? [...p.highlights] : [],
          active: p.active !== false,
        }));
      }

      snapshotState();
      showToast("Academic Programs updated successfully!", "success");
    } catch (err) {
      showToast(err.message || "Failed to update academic programs", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Form reset to saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .editor-body {
    outline: none;
  }
  .editor-body :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 0.75rem;
    margin: 1rem 0;
    border: 1px solid #e2e8f0;
    display: block;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .editor-body :deep(img:hover) {
    outline: 3px solid #6366f1;
    outline-offset: 2px;
  }
  .editor-body :deep(h1) {
    font-size: 1.875rem;
    font-weight: 900;
    color: #0f172a;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }
  .editor-body :deep(h2) {
    font-size: 1.5rem;
    font-weight: 800;
    color: #0f172a;
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
  }
  .editor-body :deep(h3) {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }
  .editor-body :deep(p) {
    margin-bottom: 0.75rem;
    color: #334155;
    line-height: 1.6;
  }
  .editor-body :deep(ul) {
    list-style-type: disc;
    padding-left: 1.25rem;
    margin-bottom: 0.75rem;
  }
  .editor-body :deep(ol) {
    list-style-type: decimal;
    padding-left: 1.25rem;
    margin-bottom: 0.75rem;
  }
  .editor-body :deep(blockquote) {
    border-left: 4px solid #6366f1;
    padding-left: 1rem;
    font-style: italic;
    color: #475569;
    margin: 1rem 0;
  }
  .editor-body :deep(a) {
    color: #4f46e5;
    text-decoration: underline;
    font-weight: 600;
  }

  .toast-enter-active,
  .toast-leave-active,
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(-0.5rem);
  }
</style>
